import React, { useState, useEffect, useRef } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { MessageSquare, Trash2, Mail, Phone, ArrowUpDown, Download, CheckCircle, XCircle, RefreshCw, AlertCircle, X, Settings, AlertTriangle, Clock } from 'lucide-react';
import { ConversationModal } from '../conversations/ConversationModal';
import { exportLeadsToCSV } from '../../utils/csvExport';
import { useChatbotCRMIntegration } from '../../hooks/useChatbotCRMIntegration';
import { useDesignColors } from '../../hooks/useDesignColors';
import { useNavigate } from 'react-router-dom';
import type { Lead } from '../../types/leads';

// Clé de stockage local pour les leads déjà interagis
const INTERACTED_LEADS_STORAGE_KEY = 'interacted_leads';

// Étendre l'interface Lead pour supporter les leads mis en évidence
interface ExtendedLead extends Lead {
  highlighted?: boolean;
}

interface LeadListProps {
  leads: ExtendedLead[];
  loading: boolean;
  error: string | null;
  onToggleStatus?: (leadId: string) => Promise<void>;
  onDeleteLeads?: (leadIds: string[]) => Promise<void>;
}

export function LeadList({ leads, loading, error, onToggleStatus, onDeleteLeads }: LeadListProps) {
  const [selectedLeads, setSelectedLeads] = useState<Set<string>>(new Set());
  const [selectedTypes, setSelectedTypes] = useState<Set<'email' | 'phone'>>(new Set());
  const [sortField, setSortField] = useState<'date' | 'type'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [processingLeads, setProcessingLeads] = useState<Set<string>>(new Set());
  const [syncingLeads, setSyncingLeads] = useState<Set<string>>(new Set());
  const [showCRMAlert, setShowCRMAlert] = useState(false);
  const { config, syncLead, loading: syncLoading } = useChatbotCRMIntegration();
  const { colors } = useDesignColors();
  const navigate = useNavigate();
  const highlightedLeadRefs = useRef<{[key: string]: HTMLDivElement | null}>({});
  
  // État pour suivre les leads avec lesquels l'utilisateur a interagi
  const [interactedLeads, setInteractedLeads] = useState<Set<string>>(new Set());

  // Vérifier si au moins un CRM est configuré et activé
  const hasCRMEnabled = config?.is_enabled ?? false;

  // État pour suivre les leads déjà synchronisés
  const [syncedLeads, setSyncedLeads] = useState<Set<string>>(new Set());

  // Charger les leads déjà interagis depuis le localStorage au démarrage
  useEffect(() => {
    try {
      const storedInteractedLeads = localStorage.getItem(INTERACTED_LEADS_STORAGE_KEY);
      if (storedInteractedLeads) {
        setInteractedLeads(new Set(JSON.parse(storedInteractedLeads)));
      }
    } catch (e) {
      console.error('Error loading interacted leads:', e);
    }
  }, []);

  // Sauvegarder les leads interagis dans le localStorage
  const saveInteractedLeads = (newInteractedLeads: Set<string>) => {
    try {
      localStorage.setItem(
        INTERACTED_LEADS_STORAGE_KEY, 
        JSON.stringify(Array.from(newInteractedLeads))
      );
      setInteractedLeads(newInteractedLeads);
    } catch (e) {
      console.error('Error saving interacted leads:', e);
    }
  };

  // Marquer un lead comme ayant interagi avec
  const markLeadAsInteracted = (leadId: string) => {
    const newInteractedLeads = new Set(interactedLeads);
    newInteractedLeads.add(leadId);
    saveInteractedLeads(newInteractedLeads);
  };

  // Scroll to highlighted lead on initial render
  useEffect(() => {
    const highlightedLead = leads.find(lead => lead.highlighted && !interactedLeads.has(lead.id));
    if (highlightedLead && highlightedLeadRefs.current[highlightedLead.id]) {
      setTimeout(() => {
        highlightedLeadRefs.current[highlightedLead.id]?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }, 300);
    }
  }, [leads, interactedLeads]);

  const handleSelectAll = () => {
    if (selectedLeads.size === filteredLeads.length) {
      setSelectedLeads(new Set());
    } else {
      setSelectedLeads(new Set(filteredLeads.map(lead => lead.id)));
    }
  };

  const handleSort = (field: 'date' | 'type') => {
    if (sortField === field) {
      setSortOrder(order => order === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleDelete = async () => {
    if (!onDeleteLeads || selectedLeads.size === 0) return;
    
    try {
      setDeleteLoading(true);
      await onDeleteLeads(Array.from(selectedLeads));
      
      // Marquer les leads supprimés comme interagis
      const newInteractedLeads = new Set(interactedLeads);
      selectedLeads.forEach(id => newInteractedLeads.add(id));
      saveInteractedLeads(newInteractedLeads);
      
      setSelectedLeads(new Set());
    } catch (err) {
      console.error('Error deleting leads:', err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleToggleStatus = async (id: string) => {
    if (!onToggleStatus) return;
    
    try {
      setProcessingLeads(prev => new Set(prev).add(id));
      await onToggleStatus(id);
      
      // Marquer le lead comme interagi
      markLeadAsInteracted(id);
    } catch (err) {
      console.error('Error toggling lead status:', err);
    } finally {
      setProcessingLeads(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleSync = async (id: string) => {
    // Si aucun CRM n'est configuré, afficher l'alerte moderne
    if (!hasCRMEnabled) {
      setShowCRMAlert(true);
      return;
    }

    try {
      // Marquer le lead comme en cours de synchronisation
      setSyncingLeads(prev => new Set(prev).add(id));
      
      // Synchroniser le lead
      await syncLead(id);
      
      // Marquer le lead comme synchronisé et interagi
      setSyncedLeads(prev => new Set(prev).add(id));
      markLeadAsInteracted(id);
    } catch (err) {
      console.error('Error syncing lead:', err);
    } finally {
      // Supprimer le lead de la liste en cours de synchronisation
      setSyncingLeads(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleViewConversation = (leadId: string, conversationId: string) => {
    markLeadAsInteracted(leadId);
    setSelectedConversationId(conversationId);
  };

  const handleNavigateToCRMSettings = () => {
    setShowCRMAlert(false);
    navigate('/app/integrations/crm');
  };

  const handleExport = () => {
    const leadsToExport = selectedLeads.size > 0
      ? filteredLeads.filter(lead => selectedLeads.has(lead.id))
      : filteredLeads;
    exportLeadsToCSV(leadsToExport);
  };

  // Filtrer et trier les leads
  const filteredLeads = leads
    .filter(lead => selectedTypes.size === 0 || selectedTypes.has(lead.type))
    .sort((a, b) => {
      if (sortField === 'date') {
        return sortOrder === 'asc'
          ? new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
      return sortOrder === 'asc'
        ? a.type.localeCompare(b.type)
        : b.type.localeCompare(a.type);
    });

  // Vérifier s'il y a des leads mis en évidence qui n'ont pas encore été interagis
  const activeHighlightedLeads = leads.filter(lead => lead.highlighted && !interactedLeads.has(lead.id));
  const hasActiveHighlightedLeads = activeHighlightedLeads.length > 0;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-violet-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-400 text-center p-4 bg-red-400/10 rounded-xl border border-red-400/20 backdrop-blur-xl text-sm md:text-base animate-fade-in">
        <AlertTriangle className="w-6 h-6 text-red-400 mx-auto mb-2" />
        An error occurred while loading the leads
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Bannière pour les leads mis en évidence */}
      {hasActiveHighlightedLeads && (
        <div className="bg-gradient-to-r from-violet-600/20 to-cyan-500/20 backdrop-blur border border-violet-500/20 rounded-lg p-3 flex items-center gap-3 animate-fade-in shadow-sm">
          <AlertTriangle className="text-white w-5 h-5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-white text-sm md:text-base">
              {activeHighlightedLeads.length === 1 
                ? 'A new lead has been highlighted below.' 
                : `${activeHighlightedLeads.length} new leads have been highlighted below.`}
            </p>
          </div>
        </div>
      )}
      
      {/* Barre d'outils */}
      <div className="flex flex-col md:flex-row gap-4 mb-4">
        <div className="flex items-center justify-between md:justify-start gap-2 md:gap-4">
          <button
            onClick={handleSelectAll}
            className="px-2 md:px-3 py-1.5 rounded-lg bg-white/5 text-gray-300 hover:bg-white/10 transition-colors text-sm shadow-sm"
          >
            {selectedLeads.size === filteredLeads.length ? 'Deselect all' : 'Select all'}
          </button>
          {selectedLeads.size > 0 && (
            <button
              onClick={handleDelete}
              disabled={deleteLoading}
              className="px-2 md:px-3 py-1.5 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors flex items-center gap-2 text-sm border border-red-500/20 shadow-sm"
            >
              <Trash2 className="w-4 h-4" />
              {deleteLoading ? 'Deletion...' : 'Delete'}
            </button>
          )}
          <span className="text-gray-400 text-sm">
            {selectedLeads.size} lead{selectedLeads.size > 1 ? 's' : ''} selected
          </span>
        </div>

        <div className="flex flex-wrap items-center justify-between md:justify-end gap-2 md:gap-4 mt-2 md:mt-0">
          {/* Filtres par type */}
          <div className="flex gap-2">
            <button
              onClick={() => {
                setSelectedTypes(prev => {
                  const next = new Set(prev);
                  if (next.has('email')) next.delete('email');
                  else next.add('email');
                  return next;
                });
              }}
              className={`flex items-center gap-1 px-2 md:px-3 py-1.5 rounded-lg transition-colors shadow-sm ${
                selectedTypes.has('email')
                  ? 'bg-gradient-to-r from-violet-600/30 to-cyan-500/30 text-white'
                  : 'bg-white/5 text-gray-300 hover:bg-white/10'
              }`}
            >
              <Mail className="w-4 h-4" />
              <span className="text-sm">Emails</span>
            </button>
            <button
              onClick={() => {
                setSelectedTypes(prev => {
                  const next = new Set(prev);
                  if (next.has('phone')) next.delete('phone');
                  else next.add('phone');
                  return next;
                });
              }}
              className={`flex items-center gap-1 px-2 md:px-3 py-1.5 rounded-lg transition-colors shadow-sm ${
                selectedTypes.has('phone')
                  ? 'bg-gradient-to-r from-violet-600/30 to-cyan-500/30 text-white'
                  : 'bg-white/5 text-gray-300 hover:bg-white/10'
              }`}
            >
              <Phone className="w-4 h-4" />
              <span className="text-sm">Phones</span>
            </button>
          </div>

          {/* Boutons de tri et d'export */}
          <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleSort('date')}
                className={`px-2 md:px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors text-sm shadow-sm ${
                  sortField === 'date' 
                    ? 'bg-gradient-to-r from-violet-600/30 to-cyan-500/30 text-white' 
                    : 'bg-white/5 text-gray-300 hover:bg-white/10'
                }`}
              >
                Date <ArrowUpDown className="w-3 h-3 md:w-4 md:h-4" />
            </button>
            <button
              onClick={() => handleSort('type')}
                className={`px-2 md:px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors text-sm shadow-sm ${
                  sortField === 'type' 
                    ? 'bg-gradient-to-r from-violet-600/30 to-cyan-500/30 text-white' 
                    : 'bg-white/5 text-gray-300 hover:bg-white/10'
                }`}
              >
                Type <ArrowUpDown className="w-3 h-3 md:w-4 md:h-4" />
            </button>
          </div>

          {/* Bouton d'export */}
          <button
            onClick={handleExport}
              className="px-2 md:px-3 py-1.5 rounded-lg bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-400 hover:from-green-500/30 hover:to-emerald-500/30 transition-colors flex items-center gap-1 text-sm whitespace-nowrap border border-green-500/20 shadow-sm"
          >
            <Download className="w-4 h-4" />
              <span className="hidden md:inline">Export</span>
          </button>
          </div>
        </div>
      </div>

      {/* Liste des leads */}
      <div className="bg-gradient-to-b from-gray-800/70 to-gray-900/70 backdrop-blur-xl rounded-xl border border-white/10 divide-y divide-white/10 overflow-hidden shadow-xl shadow-black/20">
        {filteredLeads.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-400">No leads found with the current filters</p>
          </div>
        ) : (
          filteredLeads.map((lead) => {
            // Déterminer si le lead doit être mis en évidence
            const shouldHighlight = lead.highlighted && !interactedLeads.has(lead.id);
            
            return (
          <div
            key={lead.id}
                ref={el => highlightedLeadRefs.current[lead.id] = el}
                className={`p-4 transition-all relative ${
                  shouldHighlight ? 'bg-gradient-to-r from-violet-600/10 to-cyan-500/10 backdrop-blur-lg animate-pulse-subtle' : 
                  selectedLeads.has(lead.id) ? 'bg-gradient-to-r from-violet-600/5 to-cyan-500/5 backdrop-blur-lg' : 'hover:bg-gradient-to-r hover:from-gray-700/30 hover:to-gray-800/30 backdrop-blur-lg'
                }`}
              >
                {shouldHighlight && (
                  <div className="absolute -left-1 top-0 bottom-0 w-1 bg-gradient-to-b from-violet-500 to-cyan-500 rounded-r-full animate-glow" />
                )}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="flex items-start md:items-center gap-3">
                <input
                  type="checkbox"
                  checked={selectedLeads.has(lead.id)}
                  onChange={() => {
                    setSelectedLeads(prev => {
                      const next = new Set(prev);
                      if (next.has(lead.id)) {
                        next.delete(lead.id);
                      } else {
                        next.add(lead.id);
                      }
                      return next;
                    });
                  }}
                      className="mt-1 md:mt-0 rounded-md border-white/10 bg-white/5 text-violet-500 focus:ring-violet-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className={`p-1.5 rounded-lg ${
                          lead.type === 'email'
                            ? 'bg-gradient-to-r from-violet-600/20 to-cyan-500/20'
                            : 'bg-gradient-to-r from-violet-600/20 to-cyan-500/20'
                        }`}>
                    {lead.type === 'email' ? (
                            <Mail className="w-4 h-4 text-white" />
                          ) : (
                            <Phone className="w-4 h-4 text-white" />
                          )}
                        </div>
                        <span className={`font-medium ${shouldHighlight ? 'text-white' : 'text-white'} break-all`}>
                          {lead.value}
                        </span>
                        {shouldHighlight && (
                          <span className="text-xs px-2 py-0.5 bg-gradient-to-r from-violet-600/40 to-cyan-500/40 text-white rounded-full font-medium shadow-sm">
                            New
                          </span>
                        )}
                  </div>
                      <div className="flex items-center flex-wrap gap-2 mt-2 ml-1 text-sm text-gray-400">
                    <button
                          onClick={() => handleViewConversation(lead.id, lead.conversationId)}
                          className="flex items-center gap-1.5 hover:text-white transition-colors bg-white/5 hover:bg-white/10 rounded-lg px-2 py-1 text-xs"
                    >
                          <MessageSquare className="w-3.5 h-3.5" />
                          <span>See conversation</span>
                    </button>
                        <span className="text-xs flex items-center gap-1 bg-gray-800/50 px-2 py-1 rounded-lg">
                          <Clock className="w-3 h-3 text-gray-500" />
                      {formatDistanceToNow(new Date(lead.createdAt), {
                        addSuffix: true,
                            locale: enUS
                      })}
                    </span>
                  </div>
                </div>
              </div>
                  <div className="flex flex-wrap items-center gap-2 mt-2 md:mt-0">
                <button
                  onClick={() => handleSync(lead.id)}
                  disabled={syncingLeads.has(lead.id) || syncedLeads.has(lead.id)}
                      className={`px-2 md:px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1 text-xs md:text-sm flex-1 md:flex-auto justify-center md:justify-start shadow-sm ${
                    syncedLeads.has(lead.id)
                          ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-300 border border-green-500/20'
                          : 'bg-gradient-to-r from-violet-600/20 to-cyan-500/20 text-white hover:from-violet-600/30 hover:to-cyan-500/30 border border-violet-500/20'
                  }`}
                >
                  {syncingLeads.has(lead.id) ? (
                        <div className="w-3 h-3 md:w-4 md:h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : syncedLeads.has(lead.id) ? (
                        <CheckCircle className="w-3 h-3 md:w-4 md:h-4" />
                  ) : (
                        <RefreshCw className="w-3 h-3 md:w-4 md:h-4" />
                  )}
                      {syncedLeads.has(lead.id) ? 'Synced' : 'Sync to CRM'}
                </button>
                <button
                  onClick={() => handleToggleStatus(lead.id)}
                  disabled={processingLeads.has(lead.id)}
                      className={`flex items-center gap-1 px-2 md:px-3 py-1.5 rounded-lg transition-colors text-xs md:text-sm flex-1 md:flex-auto justify-center md:justify-start shadow-sm ${
                    lead.processed
                          ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-300 border border-green-500/20'
                          : 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10'
                  }`}
                >
                  {processingLeads.has(lead.id) ? (
                        <div className="w-3 h-3 md:w-4 md:h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : lead.processed ? (
                    <>
                          <CheckCircle className="w-3 h-3 md:w-4 md:h-4" />
                          <span>Processed</span>
                    </>
                  ) : (
                    <>
                          <XCircle className="w-3 h-3 md:w-4 md:h-4" />
                          <span>Mark as processed</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
            );
          })
        )}
      </div>

      {/* Modal de conversation */}
      <ConversationModal 
        conversationId={selectedConversationId}
        onClose={() => setSelectedConversationId(null)}
      />

      {/* Alerte CRM élégante */}
      {showCRMAlert && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/60 backdrop-blur-sm">
          <div 
            className="max-w-md p-6 rounded-xl shadow-2xl animate-fade-in"
            style={{
              background: `linear-gradient(135deg, ${colors.gradientStart}99, ${colors.gradientEnd}99)`,
              boxShadow: `0 16px 48px -8px ${colors.gradientStart}60`
            }}
          >
            <div className="flex flex-col items-center gap-4">
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{
                  background: `linear-gradient(135deg, ${colors.gradientStart}30, ${colors.gradientEnd}30)`,
                }}
              >
                <AlertCircle className="w-8 h-8 text-white" />
              </div>
              
              <div className="text-center">
                <h3 className="text-xl font-bold text-white mb-2">No CRM Configured</h3>
                <p className="text-white/80 text-sm mb-4">
                  To sync your leads with a CRM system, you need to configure at least one CRM integration first. 
                  Would you like to set up your CRM integration now?
                </p>
                
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={handleNavigateToCRMSettings}
                    className="px-4 py-2 rounded-lg bg-white text-gray-900 font-medium hover:bg-white/90 transition-colors flex items-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    Configure CRM
                  </button>
                  <button
                    onClick={() => setShowCRMAlert(false)}
                    className="px-4 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}